import { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

interface Props {
  title: string;
  metaTitle: string;
  metaDescription: string;
  focusKeyword: string;
  bodyHtml: string;
  hasCoverImage: boolean;
}

interface Check {
  id: string;
  label: string;
  weight: number;
  pass: 'ok' | 'warn' | 'fail';
  hint?: string;
}

/**
 * Quick SEO heuristic — not a substitute for actual analytics, just a
 * "did the author fill in the obvious stuff?" sanity panel. Rules picked
 * from common guides (Yoast, Moz) but kept loose: warn ≠ fail, and the
 * focus-keyword checks degrade gracefully when no keyword is set.
 *
 * Score breakdown (max 100):
 *   - Title length 30-70                  15
 *   - Meta title 30-60                    15
 *   - Meta description 120-160            20
 *   - Focus keyword in title              15
 *   - Focus keyword in meta description   10
 *   - Focus keyword in body               10
 *   - Body ≥ 300 words                    10
 *   - Cover image set                      5
 */

function plainText(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function wordCount(s: string): number {
  return s ? s.trim().split(/\s+/).filter(Boolean).length : 0;
}

function contains(haystack: string, needle: string): boolean {
  if (!needle.trim()) return false;
  return haystack.toLowerCase().includes(needle.trim().toLowerCase());
}

function rangeCheck(len: number, min: number, max: number): 'ok' | 'warn' | 'fail' {
  if (len === 0) return 'fail';
  if (len >= min && len <= max) return 'ok';
  return 'warn';
}

export function SeoScorePanel({
  title,
  metaTitle,
  metaDescription,
  focusKeyword,
  bodyHtml,
  hasCoverImage,
}: Props) {
  const checks = useMemo<Check[]>(() => {
    const text = plainText(bodyHtml);
    const words = wordCount(text);
    const fk = focusKeyword.trim();
    const titleLen = title.length;
    const mtLen = metaTitle.length;
    const mdLen = metaDescription.length;

    const list: Check[] = [
      {
        id: 'title-length',
        label: `หัวข้อยาว ${titleLen} ตัวอักษร (แนะนำ 30-70)`,
        weight: 15,
        pass: rangeCheck(titleLen, 30, 70),
      },
      {
        id: 'meta-title-length',
        label: `Meta title ${mtLen} ตัวอักษร (แนะนำ 30-60)`,
        weight: 15,
        pass: rangeCheck(mtLen, 30, 60),
        hint: mtLen === 0 ? 'จะ fallback ใช้หัวข้อแทน' : undefined,
      },
      {
        id: 'meta-desc-length',
        label: `Meta description ${mdLen} ตัวอักษร (แนะนำ 120-160)`,
        weight: 20,
        pass: rangeCheck(mdLen, 120, 160),
      },
      {
        id: 'fk-in-title',
        label: 'Focus keyword อยู่ในหัวข้อ',
        weight: 15,
        pass: fk ? (contains(title, fk) ? 'ok' : 'fail') : 'warn',
        hint: !fk ? 'ตั้ง focus keyword เพื่อเช็คอัตโนมัติ' : undefined,
      },
      {
        id: 'fk-in-meta',
        label: 'Focus keyword อยู่ใน meta description',
        weight: 10,
        pass: fk ? (contains(metaDescription, fk) ? 'ok' : 'fail') : 'warn',
      },
      {
        id: 'fk-in-body',
        label: 'Focus keyword ปรากฏในเนื้อหา',
        weight: 10,
        pass: fk ? (contains(text, fk) ? 'ok' : 'fail') : 'warn',
      },
      {
        id: 'body-length',
        label: `เนื้อหา ${words} คำ (แนะนำ ≥ 300)`,
        weight: 10,
        pass: words >= 300 ? 'ok' : words >= 100 ? 'warn' : 'fail',
      },
      {
        id: 'cover-image',
        label: 'มีรูปปก',
        weight: 5,
        pass: hasCoverImage ? 'ok' : 'warn',
      },
    ];
    return list;
  }, [title, metaTitle, metaDescription, focusKeyword, bodyHtml, hasCoverImage]);

  const score = useMemo(() => {
    return checks.reduce((acc, c) => {
      if (c.pass === 'ok') return acc + c.weight;
      if (c.pass === 'warn') return acc + Math.round(c.weight * 0.5);
      return acc;
    }, 0);
  }, [checks]);

  const grade =
    score >= 80
      ? { label: 'ดี', color: 'text-emerald-600', bar: 'bg-emerald-500' }
      : score >= 50
        ? { label: 'พอใช้', color: 'text-amber-600', bar: 'bg-amber-500' }
        : { label: 'ควรปรับปรุง', color: 'text-red-600', bar: 'bg-red-500' };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
      <div className="mb-2 flex items-baseline justify-between">
        <p className="text-[11px] uppercase text-slate-400">SEO score</p>
        <div className="flex items-baseline gap-2">
          <span className={`text-2xl font-bold ${grade.color}`}>{score}</span>
          <span className="text-xs text-slate-500">/ 100 — {grade.label}</span>
        </div>
      </div>

      <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div
          className={`h-full transition-all ${grade.bar}`}
          style={{ width: `${score}%` }}
          aria-label={`คะแนน SEO ${score} จาก 100`}
        />
      </div>

      <ul className="space-y-1.5 text-xs">
        {checks.map((c) => {
          const Icon = c.pass === 'ok' ? CheckCircle2 : c.pass === 'warn' ? AlertTriangle : XCircle;
          const color =
            c.pass === 'ok'
              ? 'text-emerald-500'
              : c.pass === 'warn'
                ? 'text-amber-500'
                : 'text-red-500';
          return (
            <li key={c.id} className="flex items-start gap-2 text-slate-700 dark:text-slate-300">
              <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${color}`} />
              <span className="flex-1">
                {c.label}
                {c.hint && <span className="ml-1 text-[11px] text-slate-400">— {c.hint}</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
